import type {
  AccountConfiguration,
  BookingType,
  ExecutionCriteria,
  ExitCriteria,
  RfqProtocolParameters,
  VenueTraderInfo,
} from '@/legacy-api/orders-slice.gen'
import type {
  OrderMetadataFields,
  OrderSide,
  OrderSizeType,
  OrderStatus,
  OrderType,
} from '@/types/enums/order'
import type { AllocationStrategy } from './accounts'
import type { AccountConfigurationView } from './investment-advisor-representative'
import type { Fee } from './quotes'

import { AllocationStatus } from './events/allocation-update'

// Asset-class-specific defaults. Fixed-income flows use AoN (LPL+HT
// historical convention); equities support partial fills and default to false.
// Mutual funds execute as a single trade at the parent's full notional (no
// partial or split fills), so their executions must match the AoN parent.
export const DEFAULT_FI_ALL_OR_NONE = true
export const DEFAULT_EQUITY_ALL_OR_NONE = false
export const DEFAULT_MUTUAL_FUND_ALL_OR_NONE = true

const TERMINAL_ALLOCATION_STATUSES: ReadonlySet<AllocationStatus> = new Set([
  AllocationStatus.Rejected,
  AllocationStatus.Failed,
  AllocationStatus.Canceled,
])

/** A rejected, failed or canceled allocation stays on the order, which still needs allocating. */
export function hasLiveAllocation(order: Order): boolean {
  const status = order.allocation_info?.status
  return status !== undefined && !TERMINAL_ALLOCATION_STATUSES.has(status)
}

export interface Amount {
  type: string
  value: number
}

/**
 * Mirror of horizon's `PriceValue` schema
 * (`internal/apps/trading/internal/api/common/common-openapi.yaml`). The wire
 * delivers an object — extract `.value` to use the underlying number.
 */
export interface PriceValue {
  type: string
  value: number
  benchmark_isin?: string | null
}

// Used on the parent `Order` — legacy bare-number shape.
interface FilledAvgPrice {
  pop: number
  ytw: number | null
  ytm: number | null
}

// Used on `ExecutionOrder` — newer `PriceValue`-shaped `pop`. The BE
// serializes the same field name with different shapes depending on which
// struct holds it (parent vs. EO); we mirror that split on the FE so each
// surface gets the right reader.
interface ExecutionOrderFilledAvgPrice {
  pop: PriceValue
  ytw: number | null
  ytm: number | null
}

export interface RFQMetadata {
  id: string
}

// SHORT-TERM: Added account_configuration field to support trader bulk upload with account metadata
// LONG-TERM: This manual type will be replaced by generated Order type from orders-slice.gen.ts
export interface AccountAllocationRequest {
  client_account_allocation_id: string
  account_id: string // This is a custodian account ID
  requested_amount: OrderSize
  account_configuration?: AccountConfiguration
  // The Moment internal ID of the sleeve to allocate the order to. Emitted only
  // when the source carries a real sleeve; a falsy source omits the field.
  moment_sleeve_id?: string
}

interface AccountAllocation extends AccountAllocationRequest {
  actual_amount: OrderSize
  fill_price: number
  venue_mpid: string
  venue_execution_id: string
}

interface Trade {
  amount: OrderSize
  price: number
  venue_mpid: string
  venue_execution_id: string
}

export interface Order {
  id: string
  account_booking_type?: BookingType
  account_configuration?: AccountConfigurationView
  account_id?: string
  accrued_interest?: number
  allocation_info?: {
    id: string
    client_allocation_id: string
    client_order_id: string
    custodian_id: string
    ib_id: string
    risk_group_id: string
    order_id: string
    status: AllocationStatus
    strategy: string
    account_requests?: AccountAllocationRequest[]
    account_allocations?: AccountAllocation[]
    trades?: Trade[]
    calculated_at?: string
    submitted_at?: string
  } | null
  amount: Amount
  annotation?: string
  approved_at?: string | null
  canceled_at?: string | null
  client_order_id?: string
  control_parameters?: ControlParameters
  created_at?: string | null
  cusip: string
  execution_orders?: ExecutionOrder[]
  extended_hours: boolean
  filled_amount?: Amount
  filled_at?: string | null
  filled_avg_price?: FilledAvgPrice | null
  filled_net_money?: number
  filled_pre_fee_net_money?: number
  ib_id?: string
  is_block_trade?: boolean
  isin: string
  latest_msg_number?: string
  limit_price: number | null
  rejected_at?: string | null
  rfqs?: RFQMetadata[]
  risk_group_id?: string
  side: string
  status: OrderStatus
  type: string
  all_or_none: boolean
  is_group_order?: boolean
  group_members?: { moment_order_id: string }[]
  metadata?: OrderRequestMetadata
  fees?: Fee[]
  settlement_date?: number | null // YYYYMMDD format as integer
}

export interface OrderSize {
  value: number
  type: OrderSizeType
}

export interface OEMSAllocationsRequest {
  strategy: AllocationStrategy
  client_allocation_id: string
  account_allocations: AccountAllocationRequest[]
}

interface OEMSExecutionInstruction {
  protocol: ExecutionOrderProtocol
  strategy: ExecutionOrderStrategy
  quantity: OrderSize
  limit_price?: number
  suppress_pmp_check: boolean
  all_or_none: boolean
  venue_trader_info: VenueTraderInfo[]
  trader_profile: TraderProfileForOrder
  client_execution_order_id: string
}

export interface OEMSPlaceOrderRequest {
  instrument_id: string
  client_order_id: string
  amount: OrderSize
  side: OrderSide
  type: OrderType
  limit_price: number | null
  account_id?: string | null
  annotation?: string | null
  booking_instructions?: Record<string, string> | null
  extended_hours?: boolean
  rfq?: RFQMetadata | null
  ib_id?: string
  risk_group_id: string
  custodian_id?: string
  allocations?: OEMSAllocationsRequest | null
  execution_instructions?: OEMSExecutionInstruction[] | null
  explicit_cancelation_required?: boolean
  trader_profile?: TraderProfileForOrder
  metadata?: OrderRequestMetadata
  all_or_none?: boolean
  bypass_auto_execution?: boolean
  settlement_date?: number | null // YYYYMMDD format as integer
  order_rfq_ids?: string[]
}

export type TraderProfileForOrder = {
  user_id: string
  bloomberg_id?: string
}

export interface OEMSPlaceOrderResponse {
  id: string
  client_order_id: string
}

export interface AmountValue {
  type: string
  value: number
}

export enum ExecutionOrderProtocol {
  LOB = 'limit_order_book',
  RFQ = 'request_for_quote',
  External = 'external',
  Group = 'group',
  Custodian = 'custodian',
}

export enum ExecutionOrderStrategy {
  SMART_ORDER_ROUTING = 'smart_order_routing',
  MANUAL = 'manual',
  REPORT = 'report',
  AUTO_EX = 'auto_ex',
}

// A single fill against an execution order. The BE nests these directly on
// each `ExecutionOrder` (one entry per partial fill); prices are
// `PriceValue`-shaped like the EO's `filled_avg_price`. Note `child_order_id`
// is the venue execution id and is NOT unique per fill — multiple fills against
// the same child order share it, so it can't be used as a stable row key alone.
export interface ExecutionOrderFill {
  amount: OrderSize
  price: PriceValue
  pre_fee_price: PriceValue
  net_money: number
  pre_fee_net_money: number
  timestamp: string
  trade_date: string
  settlement_date: string
  child_order_id: string
  venue_mpid: string
  accrued_interest_cents: number
  ytm: number | null
  ytw: number | null
}

// Flattened custodian algo parameters the BE surfaces on an execution order
// submitted via an algo. This is the read-side wire shape.
export interface ExecutionOrderAlgoParameters {
  strategy: 'twap' | 'vwap' | 'pov' | 'is'
  start_time?: string | null
  end_time?: string | null
  participate_opening_auction: boolean
  participate_closing_auction: boolean
  aggressiveness?: 'passive' | 'neutral' | 'aggressive' | null
  max_participation_percent?: number | null
  target_participation_percent?: number | null
  would_price?: PriceValue | null
  would_trigger_quantity?: OrderSize | null
  would_trigger_percent?: number | null
}

export interface ExecutionOrder {
  id: string
  client_execution_order_id: string
  protocol: ExecutionOrderProtocol
  status: ExecutionOrderStatus
  amount: OrderSize
  filled_amount: OrderSize
  filled_avg_price: ExecutionOrderFilledAvgPrice
  fills?: ExecutionOrderFill[]
  created_at?: string
  updated_at?: string
  strategy: ExecutionOrderStrategy
  limit_price: PriceValue | null
  suppress_pmp_check: boolean
  rfq_metadata: {
    moment_rfq_id: string
    contra_quote_id: string | null
  } | null
  all_or_none: boolean
  rfq_protocol_parameters?: RfqProtocolParameters[]
  execution_criteria?: ExecutionCriteria
  exit_criteria?: ExitCriteria
  venue_mpid?: string
  ruleset_metadata?: {
    proposal_id: string
    proposal_version_id: string
  } | null
  algo_parameters?: ExecutionOrderAlgoParameters | null
}

export enum ExecutionOrderStatus {
  PendingExecution = 'pending_execution',
  PartiallyFilled = 'partially_filled',
  Filled = 'filled',
  Canceled = 'canceled',
  Replaced = 'replaced',
}

// biome-ignore lint/complexity/noBannedTypes: Bulk suppression as part of biome migration
export type CancelOrderExecutionResponse = {}

export interface CancelOrderExecutionRequest {
  order_id: string
  execution_order_id: string
}

interface DenominationValue {
  value: number
  type: 'par' // TODO: Add other types
}

interface ControlParameters {
  min_price?: number | null
  max_price?: number | null
  min_denomination?: DenominationValue
}

export type OrderRequestMetadata = {
  [K in OrderMetadataFields]?: string
} & {
  [key: string]: string | undefined
}
